import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createVNode as _createVNode, unref as _unref, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-slate-200 overflow-hidden" }
const _hoisted_2 = { class: "lg:hidden mt-4 text-center" }
const _hoisted_3 = { class: "text-xs text-slate-500" }
const _hoisted_4 = { class: "font-semibold text-slate-700" }

import type { PaginacionFilterParams } from "@/utils/PaginacionFilterParams";
import { computed, ref } from "vue";
import CategoriaTable from "../components/categoriaComponents/CategoriaTable.vue";
import EmptyState from "../components/categoriaComponents/EmptyState.vue";
import ErrorState from "@/share/components/ErrorState.vue";
import FormularioCategoria from "../components/categoriaComponents/FormularioCategoria.vue";
import { useCategoriasProductoList } from "../composables/categoriaComposables";
import { CategoriaCreateInterface } from "../interfaces/categoriaInterfaces/CategoriaCreate.interface";
import { useCategoriaProductoCreate } from "../composables/categoriaComposables/useCategoriaProductoCreate";
import { useCategoriaProductoToggleVisib } from "../composables/categoriaComposables";
import { useCategoriaProductoDelete } from "../composables/categoriaComposables";
import { useCategoriaProductoUpdate } from "../composables/categoriaComposables";
import SkeletonTablas from "@/share/components/SkeletonTablas.vue";
import PaginationCompontens from "@/share/components/PaginationCompontens.vue";
import FiltrosComponents from "@/share/components/filtrosComponents.vue";
import AdminPageShell from "@/share/components/admin/AdminPageShell.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'CategoriaView',
  setup(__props) {

const params = ref<PaginacionFilterParams>({
  page: 1,
  limit: 10,
  isVisible: undefined,
  search: "",
});

const sortField = ref<
  | "nombreCategoria"
  | "descripcionCategoria"
  | "isVisible"
  | "state"
  | "createdAt"
  | null
>(null);
const sortDirection = ref<"asc" | "desc">("asc");
const isFormularioOpen = ref(false);
const categoriaEditId = ref<string | undefined>(undefined);
const isDelete = ref(false);

const {
  categorias,
  total,
  totalPages,
  currentPage,
  isPending: isLoadingList,
  isError,
  error,
  refetch,
} = useCategoriasProductoList(params);

const { mutate: crearCategoria, isPending: isCreatingCategoria } =
  useCategoriaProductoCreate(() => {
    isFormularioOpen.value = false;
  });
const { mutate: toggleVisibility } = useCategoriaProductoToggleVisib();
const { mutate: deleteCategoria } = useCategoriaProductoDelete(() => {
  isDelete.value = true;
  setTimeout(() => (isDelete.value = false), 100);
});
const { mutate: updateCategoria, isPending: isUpdatingCategoria } =
  useCategoriaProductoUpdate(() => {
    isFormularioOpen.value = false;
    categoriaEditId.value = undefined;
  });

const isSavingCategoria = computed(() =>
  categoriaEditId.value ? isUpdatingCategoria.value : isCreatingCategoria.value
);

const categoriasOrdenadas = computed(() => {
  let data = [...categorias.value];

  const field = sortField.value; // 👈 guardamos el valor para evitar el uso de "!"
  if (field) {
    data.sort((a, b) => {
      const aVal = a[field] ?? "";
      const bVal = b[field] ?? "";

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortDirection.value === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection.value === "asc"
        ? aVal > bVal
          ? 1
          : -1
        : aVal < bVal
        ? 1
        : -1;
    });
  }

  return data;
});

const handleSearch = (search: string) => {
  params.value.search = search;
  params.value.page = 1;
};

const handleVisibilityFilter = (visibility: number | undefined) => {
  params.value.isVisible = visibility;
  params.value.page = 1;
};

const handlePageChange = (page: number) => {
  params.value.page = page;
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const handleLimitChange = (limit: number) => {
  params.value.limit = limit;
  params.value.page = 1;
};

const handleSort = (
  field:
    | "nombreCategoria"
    | "descripcionCategoria"
    | "isVisible"
    | "state"
    | "createdAt"
) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortField.value = field;
    sortDirection.value = "asc";
  }
};

const handleToggleVisibility = async (categoriaId: string) => {
  toggleVisibility(categoriaId);
};

const handleOpenCreate = () => {
  categoriaEditId.value = undefined;
  isFormularioOpen.value = true;
};

const handleEditCategoria = (categoriaId: string) => {
  categoriaEditId.value = categoriaId;
  isFormularioOpen.value = true;
};

const handleSaveCategoria = (categoria: CategoriaCreateInterface) => {
  crearCategoria(categoria);
};

const handleUpdateCategoria = (
  id: string,
  categoria: CategoriaCreateInterface
) => {
  updateCategoria({
    idCategoria: id,
    categoria: categoria,
  });
};

const handleCloseFormulario = () => {
  isFormularioOpen.value = false;
  categoriaEditId.value = undefined;
};

const handleDeleteCategoria = async (categoriaId: string) => {
  deleteCategoria(categoriaId);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AdminPageShell, {
    title: "Categorías de Productos",
    description: "Administra y organiza tu catálogo de productos."
  }, {
    actions: _withCtx(() => [
      _createElementVNode("button", {
        type: "button",
        onClick: handleOpenCreate,
        class: "w-full sm:w-auto px-4 sm:px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-shadow flex items-center justify-center gap-2"
      }, [...(_cache[0] || (_cache[0] = [
        _createElementVNode("svg", {
          class: "w-5 h-5 flex-shrink-0",
          fill: "none",
          stroke: "currentColor",
          viewBox: "0 0 24 24"
        }, [
          _createElementVNode("path", {
            "stroke-linecap": "round",
            "stroke-linejoin": "round",
            "stroke-width": "2",
            d: "M12 4v16m8-8H4"
          })
        ], -1),
        _createElementVNode("span", null, "Nueva categoría", -1)
      ]))])
    ]),
    default: _withCtx(() => [
      _createVNode(FormularioCategoria, {
        isOpen: isFormularioOpen.value,
        categoriaId: categoriaEditId.value,
        "is-saving": isSavingCategoria.value,
        onClose: handleCloseFormulario,
        onSave: handleSaveCategoria,
        onUpdate: handleUpdateCategoria
      }, null, 8, ["isOpen", "categoriaId", "is-saving"]),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(FiltrosComponents, {
          search: params.value.search,
          visibility: params.value.isVisible,
          limit: params.value.limit,
          total: _unref(total),
          onSearch: handleSearch,
          onVisibilityFilter: handleVisibilityFilter,
          onLimitChange: handleLimitChange,
          onRefresh: _unref(refetch)
        }, null, 8, ["search", "visibility", "limit", "total", "onRefresh"]),
        (_unref(isLoadingList))
          ? (_openBlock(), _createBlock(SkeletonTablas, { key: 0 }))
          : (_unref(isError))
            ? (_openBlock(), _createBlock(ErrorState, {
                key: 1,
                error: _unref(error),
                onRetry: _unref(refetch)
              }, null, 8, ["error", "onRetry"]))
            : (_unref(categorias).length > 0)
              ? (_openBlock(), _createBlock(CategoriaTable, {
                  key: 2,
                  categorias: categoriasOrdenadas.value,
                  "sort-field": sortField.value,
                  "sort-direction": sortDirection.value,
                  currentPage: _unref(currentPage),
                  pageSize: params.value.limit,
                  isDelete: isDelete.value,
                  onSort: handleSort,
                  onToggleVisibility: handleToggleVisibility,
                  onEditCategoria: handleEditCategoria,
                  onDeleteCategoria: handleDeleteCategoria
                }, null, 8, ["categorias", "sort-field", "sort-direction", "currentPage", "pageSize", "isDelete"]))
              : (_openBlock(), _createBlock(EmptyState, { key: 3 })),
        (!_unref(isLoadingList) && !_unref(isError) && _unref(total) > 0)
          ? (_openBlock(), _createBlock(PaginationCompontens, {
              key: 4,
              "current-page": _unref(currentPage),
              "total-pages": _unref(totalPages),
              total: _unref(total),
              limit: params.value.limit!,
              onPageChange: handlePageChange
            }, null, 8, ["current-page", "total-pages", "total", "limit"]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("p", _hoisted_3, [
          _cache[1] || (_cache[1] = _createTextVNode(" Total de categorías: ", -1)),
          _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(total)), 1)
        ])
      ])
    ]),
    _: 1
  }))
}
}

})